import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { getAllPosts, getPostsByCategory, searchPosts, categories } from '../../data/blogPosts';
import useSEO from '../../hooks/useSEO';

export default function BlogPage() {
  const [selectedCategory, setSelectedCategory] = useState('Todos');
  const [searchQuery, setSearchQuery] = useState('');

  // SEO dinámico
  useSEO({
    title: "Blog SEO - Últimas Tendencias en SEO con IA | SEOgenia",
    description: "Mantente al día con las últimas novedades, actualizaciones y estrategias del mundo SEO. Artículos sobre SEO con IA Generativa, ChatGPT, Perplexity, Google SGE y más.",
    keywords: "blog seo, tendencias seo 2025, seo ia generativa, chatgpt seo, google sge, perplexity seo, noticias seo",
    canonical: "https://seogenia.es/blog",
    image: "https://public.readdy.ai/ai/img_res/1fb1fe616d966e6bfbd98b27dc4cea3a.jpg",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Blog",
      "name": "Blog SEO - SEOgenia",
      "description": "Últimas tendencias y estrategias en SEO con IA Generativa",
      "url": "https://seogenia.es/blog",
      "publisher": {
        "@type": "Organization",
        "name": "SEOgenia",
        "url": "https://seogenia.es"
      }
    }
  });

  // Obtener artículos filtrados
  const getFilteredPosts = () => {
    if (searchQuery) {
      return searchPosts(searchQuery);
    }
    return getPostsByCategory(selectedCategory);
  };

  const posts = getFilteredPosts();

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-cyan-50 to-pink-50 rounded-full mb-6">
              <i className="ri-newspaper-line text-lg bg-gradient-to-r from-cyan-500 to-pink-500 bg-clip-text text-transparent"></i>
              <span className="text-sm font-semibold bg-gradient-to-r from-cyan-500 to-pink-500 bg-clip-text text-transparent">
                Blog SEO
              </span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Últimas Tendencias en SEO
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              Mantente al día con las últimas novedades, actualizaciones y estrategias del mundo SEO
            </p>

            {/* Search Bar */}
            <div className="max-w-2xl mx-auto">
              <div className="relative">
                <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-xl"></i>
                <input
                  type="text"
                  placeholder="Buscar artículos..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 bg-white border-2 border-gray-200 rounded-full text-gray-900 placeholder-gray-400 focus:outline-none focus:border-cyan-500 transition-colors duration-300"
                />
              </div>
            </div>
          </div>

          {/* Categories */}
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((cat) => (
              <button
                key={cat.name}
                onClick={() => {
                  setSelectedCategory(cat.name);
                  setSearchQuery('');
                }}
                className={`px-6 py-3 rounded-full font-medium transition-all duration-300 flex items-center gap-2 whitespace-nowrap ${
                  selectedCategory === cat.name
                    ? 'bg-gradient-to-r from-cyan-500 to-pink-500 text-white shadow-lg scale-105'
                    : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                }`}
              >
                <i className={`${cat.icon} text-lg`}></i>
                {cat.name}
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  selectedCategory === cat.name
                    ? 'bg-white/20'
                    : 'bg-gray-100'
                }`}>
                  {cat.count}
                </span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {posts.length === 0 ? (
            <div className="text-center py-16">
              <i className="ri-search-line text-6xl text-gray-300 mb-4"></i>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">No se encontraron artículos</h3>
              <p className="text-gray-600">Intenta con otra búsqueda o categoría</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((post, index) => (
                <Link
                  key={index}
                  to={`/blog/${post.slug}`}
                  className="group bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 cursor-pointer"
                >
                  <div className="relative h-56 overflow-hidden">
                    <img 
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    {post.isAI && (
                      <div className="absolute top-3 right-3">
                        <span className="px-3 py-1.5 bg-white/90 backdrop-blur-sm text-gray-700 text-xs font-medium rounded-full shadow-md flex items-center gap-1.5">
                          <i className="ri-robot-line text-cyan-500"></i>
                          Actualizado por IA
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="px-3 py-1 bg-cyan-50 text-cyan-600 text-xs font-medium rounded-full">
                        {post.category}
                      </span>
                      <span className="text-xs text-gray-500">{post.date}</span>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-cyan-500 group-hover:to-pink-500 group-hover:bg-clip-text transition-all duration-300 line-clamp-2">
                      {post.title}
                    </h3>
                    <p className="text-sm text-gray-600 mb-4 line-clamp-3">
                      {post.excerpt}
                    </p>
                    <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                      <div className="flex items-center gap-4 text-xs text-gray-500">
                        <span className="flex items-center gap-1.5">
                          <i className="ri-time-line"></i>
                          {post.readTime}
                        </span>
                        {post.views && (
                          <span className="flex items-center gap-1.5">
                            <i className="ri-eye-line"></i>
                            {post.views}
                          </span>
                        )}
                        {post.comments && (
                          <span className="flex items-center gap-1.5">
                            <i className="ri-chat-3-line"></i>
                            {post.comments}
                          </span>
                        )}
                      </div>
                      <span className="text-cyan-500 font-semibold group-hover:translate-x-1 transition-transform duration-300 flex items-center gap-1 text-sm whitespace-nowrap">
                        Leer
                        <i className="ri-arrow-right-line"></i>
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-white rounded-2xl p-12 shadow-xl border border-gray-100">
            <i className="ri-mail-line text-5xl bg-gradient-to-r from-cyan-500 to-pink-500 bg-clip-text text-transparent mb-6"></i>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Suscríbete a nuestro Newsletter
            </h2>
            <p className="text-gray-600 mb-8">
              Recibe las últimas tendencias SEO directamente en tu bandeja de entrada
            </p>
            <form className="flex flex-col sm:flex-row gap-4 max-w-xl mx-auto">
              <input
                type="email"
                placeholder="tu@email.com"
                className="flex-1 px-6 py-4 bg-gray-50 border border-gray-200 rounded-full text-gray-900 placeholder-gray-400 focus:outline-none focus:border-cyan-500 transition-colors duration-300"
              />
              <button 
                type="submit"
                className="w-full px-6 py-3 bg-cyan-500 text-white rounded-xl font-medium hover:shadow-lg hover:scale-105 transition-all duration-300 whitespace-nowrap"
              >
                Suscribirme
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Trending Topics */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Temas Trending
          </h2>
          <div className="flex flex-wrap justify-center gap-3">
            {[
              { tag: 'Google SGE', rank: 1 },
              { tag: 'Zero-Click', rank: 2 },
              { tag: 'E-E-A-T', rank: 3 },
              { tag: 'Voice Commerce', rank: 4 },
              { tag: 'Perplexity AI', rank: 5 },
              { tag: 'Core Web Vitals', rank: 6 },
              { tag: 'Schema Markup', rank: 7 },
              { tag: 'Gemini Ultra', rank: 8 }
            ].map((item) => (
              <button
                key={item.tag}
                className="px-5 py-2.5 bg-white border-2 border-gray-200 rounded-full text-gray-700 font-medium hover:border-cyan-500 hover:text-cyan-500 transition-all duration-300 flex items-center gap-2 whitespace-nowrap"
              >
                <span className="w-6 h-6 flex items-center justify-center bg-gradient-to-r from-cyan-500 to-pink-500 text-white text-xs font-bold rounded-full">
                  {item.rank}
                </span>
                {item.tag}
              </button>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
